import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  ReactFlow,
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  Handle,
  Position,
  Panel,
} from "reactflow";
import "reactflow/dist/style.css";

const API_URL = process.env.REACT_APP_API_BASE || "";

// ─── Token helper ─────────────────────────────────────────────────────────────
function getToken() {
  if (typeof window !== "undefined" && typeof window.locatorgarsotrga === "function") {
    return window.locatorgarsotrga("token") || null;
  }
  return typeof window !== "undefined" ? localStorage.getItem("token") : null;
}

// ─── Node type colors ─────────────────────────────────────────────────────────
const NODE_STYLES = {
  message:   { bg: "#f0faf8", border: "#ed7b00", icon: "💬", label: "Message"   },
  condition: { bg: "#fffbe6", border: "#f59e0b", icon: "🔀", label: "Condition" },
  template:  { bg: "#e0f2fe", border: "#0369a1", icon: "📋", label: "Template"  },
  input:     { bg: "#f3e8ff", border: "#7c3aed", icon: "✏️", label: "User Input" },
  delay:     { bg: "#fef2f2", border: "#ed5050", icon: "⏱️", label: "Delay"      },
  end:       { bg: "#f3f4f6", border: "#6b7280", icon: "🔚", label: "End"        },
};

// ─── Base custom node ─────────────────────────────────────────────────────────
function BaseNode({ data, selected, type }) {
  const style = NODE_STYLES[type] || NODE_STYLES.message;
  return (
    <div style={{
      background: style.bg,
      border: `2px solid ${selected ? "#316bb0" : style.border}`,
      borderRadius: 12,
      minWidth: 200,
      maxWidth: 240,
      boxShadow: selected ? "0 0 0 3px rgba(49,107,176,0.2)" : "0 2px 8px rgba(0,0,0,0.08)",
      fontFamily: "system-ui, sans-serif",
      fontSize: 13,
      cursor: "pointer",
    }}>
      <div style={{
        background: style.border,
        borderRadius: "10px 10px 0 0",
        padding: "6px 10px",
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}>
        <span style={{ fontSize: 14 }}>{style.icon}</span>
        <span style={{ color: "#fff", fontWeight: 600, fontSize: 12 }}>{style.label}</span>
        {data.label && data.label !== style.label && (
          <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 11, marginLeft: "auto", maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {data.label}
          </span>
        )}
      </div>

      <div style={{ padding: "8px 10px" }}>
        {data.text && (
          <p style={{ margin: "0 0 6px", color: "#374151", lineHeight: 1.4, fontSize: 12, wordBreak: "break-word" }}>
            {data.text.length > 80 ? data.text.slice(0, 80) + "…" : data.text}
          </p>
        )}
        {type === "template" && data.templateName && (
          <span style={{ fontSize: 11, background: "#d1fae5", color: "#065f46", padding: "2px 6px", borderRadius: 4, fontWeight: 600 }}>
            {data.templateName}
          </span>
        )}
        {type === "condition" && data.condition && (
          <code style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", padding: "2px 6px", borderRadius: 4, display: "block" }}>
            {data.condition}
          </code>
        )}
        {type === "input" && data.variable && (
          <span style={{ fontSize: 11, background: "#ede9fe", color: "#5b21b6", padding: "2px 6px", borderRadius: 4 }}>
            → {data.variable}
          </span>
        )}
        {type === "delay" && data.seconds && (
          <span style={{ fontSize: 11, color: "#ed5050", fontWeight: 600 }}>
            Wait {data.seconds}s
          </span>
        )}
        {data.buttons && data.buttons.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
            {data.buttons.map((btn, i) => (
              <span key={i} style={{
                fontSize: 11, border: "1px solid " + style.border, color: style.border,
                padding: "2px 8px", borderRadius: 10, fontWeight: 500
              }}>{btn}</span>
            ))}
          </div>
        )}
      </div>

      <Handle type="target" position={Position.Top}    style={{ background: style.border, width: 10, height: 10 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: style.border, width: 10, height: 10 }} />
      {type === "condition" && (
        <>
          <Handle type="source" position={Position.Left}  id="false" style={{ background: "#ed5050", width: 10, height: 10, top: "70%" }} />
          <Handle type="source" position={Position.Right} id="true"  style={{ background: "#ed7b00", width: 10, height: 10, top: "70%" }} />
        </>
      )}
    </div>
  );
}

const MessageNode   = (p) => <BaseNode {...p} type="message"   />;
const ConditionNode = (p) => <BaseNode {...p} type="condition" />;
const TemplateNode  = (p) => <BaseNode {...p} type="template"  />;
const InputNode     = (p) => <BaseNode {...p} type="input"     />;
const DelayNode     = (p) => <BaseNode {...p} type="delay"     />;
const EndNode       = (p) => <BaseNode {...p} type="end"       />;

const nodeTypes = { message: MessageNode, condition: ConditionNode, template: TemplateNode, input: InputNode, delay: DelayNode, end: EndNode };

// ─── Node editor panel ────────────────────────────────────────────────────────
function NodeEditor({ node, onUpdate, onClose, onDelete }) {
  const [data, setData] = useState({ ...node.data });
  const [btnInput, setBtnInput] = useState("");

  const update = (key, val) => setData(d => ({ ...d, [key]: val }));

  const addButton = () => {
    if (!btnInput.trim()) return;
    setData(d => ({ ...d, buttons: [...(d.buttons || []), btnInput.trim()] }));
    setBtnInput("");
  };

  const removeButton = (i) => setData(d => ({ ...d, buttons: d.buttons.filter((_, idx) => idx !== i) }));

  const save = () => { onUpdate(node.id, data); onClose(); };

  const style = NODE_STYLES[node.type] || NODE_STYLES.message;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,0.4)",
      display: "flex", alignItems: "center", justifyContent: "center"
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, width: 420, maxHeight: "90vh",
        overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)"
      }}>
        <div style={{ background: style.border, borderRadius: "16px 16px 0 0", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>{style.icon}</span>
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Edit {style.label} Node</span>
          </div>
          <button onClick={onClose} style={{ color: "rgba(255,255,255,0.8)", background: "none", border: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          <label style={labelStyle}>Node Label</label>
          <input value={data.label || ""} onChange={e => update("label", e.target.value)} style={inputStyle} placeholder="e.g. Welcome message" />

          {(node.type === "message" || node.type === "template" || node.type === "input") && (
            <>
              <label style={labelStyle}>Message Text</label>
              <textarea value={data.text || ""} onChange={e => update("text", e.target.value)}
                rows={4} style={{ ...inputStyle, resize: "vertical" }}
                placeholder="Type your WhatsApp message here. Use {{variable}} for dynamic content." />
            </>
          )}

          {node.type === "template" && (
            <>
              <label style={labelStyle}>Template Name</label>
              <input value={data.templateName || ""} onChange={e => update("templateName", e.target.value)} style={inputStyle} placeholder="e.g. welcome_message" />
              <label style={labelStyle}>Language Code</label>
              <input value={data.language || "en"} onChange={e => update("language", e.target.value)} style={inputStyle} placeholder="en" />
            </>
          )}

          {node.type === "condition" && (
            <>
              <label style={labelStyle}>Condition Expression</label>
              <input value={data.condition || ""} onChange={e => update("condition", e.target.value)} style={inputStyle} placeholder='e.g. {{reply}} == "yes"' />
              <p style={{ fontSize: 12, color: "#808080", marginTop: 4 }}>Left handle = false, Right handle = true</p>
            </>
          )}

          {node.type === "input" && (
            <>
              <label style={labelStyle}>Save reply to variable</label>
              <input value={data.variable || ""} onChange={e => update("variable", e.target.value)} style={inputStyle} placeholder="e.g. user_name" />
            </>
          )}

          {node.type === "delay" && (
            <>
              <label style={labelStyle}>Wait (seconds)</label>
              <input type="number" min={1} max={300} value={data.seconds || 5} onChange={e => update("seconds", parseInt(e.target.value))} style={inputStyle} />
            </>
          )}

          {(node.type === "message" || node.type === "input") && (
            <>
              <label style={labelStyle}>Quick Reply Buttons (max 3)</label>
              {(data.buttons || []).map((btn, i) => (
                <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                  <span style={{ flex: 1, background: "#f3f4f6", padding: "6px 10px", borderRadius: 6, fontSize: 13 }}>{btn}</span>
                  <button onClick={() => removeButton(i)} style={{ background: "#fee2e2", color: "#ed5050", border: "none", borderRadius: 6, padding: "0 10px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>✕</button>
                </div>
              ))}
              {(data.buttons || []).length < 3 && (
                <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                  <input value={btnInput} onChange={e => setBtnInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addButton()}
                    style={{ ...inputStyle, margin: 0, flex: 1 }} placeholder="Button text..." />
                  <button onClick={addButton} style={{ background: style.border, color: "#fff", border: "none", borderRadius: 8, padding: "0 14px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Add</button>
                </div>
              )}
            </>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 24 }}>
            <button onClick={save} style={{ flex: 1, background: style.border, color: "#fff", border: "none", borderRadius: 10, padding: "10px 0", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
              Save Node
            </button>
            <button onClick={() => { onDelete(node.id); onClose(); }} style={{ background: "#fee2e2", color: "#ed5050", border: "none", borderRadius: 10, padding: "10px 16px", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6, marginTop: 14 };
const inputStyle = { width: "100%", border: "1px solid #d4cfcf", borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none", boxSizing: "border-box" };

function SidebarItem({ type, onClick }) {
  const s = NODE_STYLES[type];
  return (
    <button onClick={onClick} style={{
      width: "100%", display: "flex", alignItems: "center", gap: 10,
      background: s.bg, border: `1.5px solid ${s.border}`, borderRadius: 10,
      padding: "10px 12px", marginBottom: 8, cursor: "grab", textAlign: "left", transition: "all 0.15s"
    }}
      onMouseOver={e => e.currentTarget.style.transform = "translateY(-1px)"}
      onMouseOut={e => e.currentTarget.style.transform = "translateY(0)"}
    >
      <span style={{ fontSize: 18 }}>{s.icon}</span>
      <div>
        <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#374151" }}>{s.label}</p>
        <p style={{ margin: 0, fontSize: 11, color: "#808080" }}>{nodeDesc[type]}</p>
      </div>
    </button>
  );
}

const nodeDesc = {
  message:   "Send a text message",
  condition: "Branch on condition",
  template:  "Send WA template",
  input:     "Capture user reply",
  delay:     "Wait before next step",
  end:       "End the flow",
};

// ─── Shape converters ─────────────────────────────────────────────────────────

// DB → ReactFlow
function dbNodeToReactFlow(node) {
  return {
    id:       String(node.id),
    type:     node.node_type || "message",
    position: {
      x: Number(node.position_x) || 0,
      y: Number(node.position_y) || 0,
    },
    data: {
      label:        node.label        || "",
      text:         node.message_text || "",
      // options from DB are [{option_text, option_value}] → flatten to string[]
      buttons:      (node.options || []).map(o => o.option_text || o),
      templateName: node.template_name || "",
      condition:    node.condition     || "",
      variable:     node.variable      || "",
      seconds:      node.seconds       || 5,
    },
  };
}

// ReactFlow → DB shape for save
function reactFlowNodeToDb(node) {
  return {
    // Use the string id as tempId so the backend idMap can resolve edges
    id:           node.id,
    tempId:       node.id,
    node_type:    node.type     || "message",
    label:        node.data?.label        || "",
    message_text: node.data?.text         || "",
    position_x:   Math.round(node.position?.x ?? 0),
    position_y:   Math.round(node.position?.y ?? 0),
    // Convert string[] buttons back to [{option_text, option_value}]
    options: (node.data?.buttons || []).map(btn => ({
      option_text:  btn,
      option_value: btn,
    })),
    // Extra fields the backend may use
    template_name: node.data?.templateName || "",
    condition:     node.data?.condition    || "",
    variable:      node.data?.variable     || "",
    seconds:       node.data?.seconds      || null,
  };
}

// ReactFlow edge → DB shape
function reactFlowEdgeToDb(edge) {
  return {
    source:          edge.source,
    target:          edge.target,
    sourceTempId:    edge.source,
    targetTempId:    edge.target,
    label:           edge.label           || "",
    condition_value: edge.conditionValue  || "",
  };
}

// ─── Main inner component ─────────────────────────────────────────────────────
const ChatbotflowbuilderInner = () => {
  const { id } = useParams();
  const token = getToken();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [saveMsg,     setSaveMsg]     = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  const [flowName,    setFlowName]    = useState("Untitled Flow");
  const reactFlowWrapper = useRef(null);

  // ── Fetch flow ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!id) { setLoading(false); return; }
    setLoading(true);
    fetch(`${API_URL}/auth/chatbot/flow/${id}/builder`, {
      headers: token ? { Authorization: token } : {},
    })
      .then(r => r.json())
      .then(json => {
        if (json.success) {
          // ✅ Convert DB shape → ReactFlow shape
          const normalizedNodes = (json.nodes || []).map(dbNodeToReactFlow);

          // ✅ Convert DB edge shape → ReactFlow shape
          const normalizedEdges = (json.edges || []).map(edge => ({
            id:       String(edge.id),
            source:   String(edge.source_node_id),
            target:   String(edge.target_node_id),
            label:    edge.label || "",
            animated: true,
            style:    { stroke: "#ed7b00", strokeWidth: 2 },
          }));

          setNodes(normalizedNodes);
          setEdges(normalizedEdges);
          if (json.name) setFlowName(json.name);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id, token]); // eslint-disable-line

  const onConnect = useCallback(
    (params) => setEdges(eds => addEdge({ ...params, animated: true, style: { stroke: "#ed7b00" } }, eds)),
    [setEdges]
  );

  const addNode = (type) => {
    const s = NODE_STYLES[type];
    const id_ = `${type}_${Date.now()}`;
    setNodes(nds => [
      ...nds,
      {
        id:       id_,
        type,
        position: { x: 200 + Math.random() * 200, y: 100 + nds.length * 140 },
        data:     { label: s.label, text: "", buttons: [] },
      }
    ]);
  };

  const updateNodeData = (nodeId, newData) => {
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data: newData } : n));
  };

  const deleteNode = (nodeId) => {
    setNodes(nds => nds.filter(n  => n.id !== nodeId));
    setEdges(eds => eds.filter(e  => e.source !== nodeId && e.target !== nodeId));
  };

  const onNodeClick = useCallback((_, node) => setEditingNode(node), []);

  // ── Save flow ───────────────────────────────────────────────────────────────
  const saveFlow = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      // ✅ Convert ReactFlow shape → DB shape before sending
      const dbNodes = nodes.map(reactFlowNodeToDb);
      const dbEdges = edges.map(reactFlowEdgeToDb);

      const res = await fetch(`${API_URL}/auth/chatbot/flow/${id}/save`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: token } : {}) },
        body:    JSON.stringify({ nodes: dbNodes, edges: dbEdges, name: flowName }),
      });
      const json = await res.json();

      if (json.success) {
        // ✅ After saving, reload from API so IDs are real DB IDs
        // This prevents "ghost" temp IDs on next open
        const reload = await fetch(`${API_URL}/auth/chatbot/flow/${id}/builder`, {
          headers: token ? { Authorization: token } : {},
        });
        const reloaded = await reload.json();
        if (reloaded.success) {
          setNodes((reloaded.nodes || []).map(dbNodeToReactFlow));
          setEdges((reloaded.edges || []).map(edge => ({
            id:       String(edge.id),
            source:   String(edge.source_node_id),
            target:   String(edge.target_node_id),
            label:    edge.label || "",
            animated: true,
            style:    { stroke: "#ed7b00", strokeWidth: 2 },
          })));
        }
        setSaveMsg("✅ Saved!");
      } else {
        setSaveMsg("❌ Save failed");
      }
    } catch {
      setSaveMsg("❌ Network error");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 3000);
    }
  };

  const clearCanvas = () => {
    if (window.confirm("Clear all nodes and edges?")) {
      setNodes([]); setEdges([]);
    }
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", flexDirection: "column", gap: 12 }}>
      <div style={{ width: 40, height: 40, border: "4px solid #e5e7eb", borderTop: "4px solid #ed7b00", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <p style={{ color: "#808080", fontSize: 14 }}>Loading flow builder...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "system-ui, sans-serif" }}>

      {/* ── LEFT SIDEBAR ── */}
      <div style={{
        width: 240, borderRight: "1px solid #e5e7eb", background: "#fff",
        display: "flex", flexDirection: "column", overflow: "hidden"
      }}>
        <div style={{ padding: "16px 14px 12px", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "#ed7b00", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 14 }}>🤖</span>
            </div>
            <span style={{ fontWeight: 700, fontSize: 14, color: "#121519" }}>Flow Builder</span>
          </div>
          <input
            value={flowName}
            onChange={e => setFlowName(e.target.value)}
            style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 6, padding: "5px 8px", fontSize: 12, color: "#374151", outline: "none", boxSizing: "border-box" }}
            placeholder="Flow name..."
          />
        </div>

        <div style={{ flex: 1, padding: "12px 10px", overflowY: "auto" }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: "#808080", textTransform: "uppercase", letterSpacing: 1, margin: "0 0 8px 2px" }}>Add Nodes</p>
          {Object.keys(NODE_STYLES).map(type => (
            <SidebarItem key={type} type={type} onClick={() => addNode(type)} />
          ))}
        </div>

        <div style={{ padding: "10px 12px", borderTop: "1px solid #e5e7eb", background: "#f9fafb" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {[{ label: "Nodes", value: nodes.length }, { label: "Edges", value: edges.length }].map(s => (
              <div key={s.label} style={{ textAlign: "center", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 0" }}>
                <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#ed7b00" }}>{s.value}</p>
                <p style={{ margin: 0, fontSize: 10, color: "#808080" }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CANVAS ── */}
      <div ref={reactFlowWrapper} style={{ flex: 1, position: "relative" }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          deleteKeyCode="Delete"
          defaultEdgeOptions={{ animated: true, style: { stroke: "#ed7b00", strokeWidth: 2 } }}
        >
          <MiniMap nodeStrokeWidth={2} zoomable pannable style={{ background: "#f9fafb", border: "1px solid #e5e7eb" }} />
          <Controls />
          <Background color="#e5e7eb" gap={20} />

          <Panel position="top-right">
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {saveMsg && (
                <span style={{ fontSize: 13, fontWeight: 600, color: saveMsg.startsWith("✅") ? "#ed7b00" : "#ed5050", background: "#fff", padding: "6px 12px", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                  {saveMsg}
                </span>
              )}
              <button onClick={clearCanvas} style={toolbarBtnStyle("#f3f4f6", "#374151")}>🗑 Clear</button>
              <button onClick={saveFlow} disabled={saving} style={toolbarBtnStyle("#ed7b00", "#fff")}>
                {saving ? "Saving…" : "💾 Save Flow"}
              </button>
            </div>
          </Panel>

          {nodes.length === 0 && (
            <Panel position="top-center">
              <div style={{ background: "#fff", border: "1px dashed #d4d4d4", borderRadius: 12, padding: "24px 32px", textAlign: "center", marginTop: 80 }}>
                <p style={{ fontSize: 32, margin: "0 0 8px" }}>🤖</p>
                <p style={{ fontSize: 15, fontWeight: 600, color: "#374151", margin: "0 0 4px" }}>Start building your flow</p>
                <p style={{ fontSize: 13, color: "#808080", margin: 0 }}>Click a node type on the left to add it</p>
              </div>
            </Panel>
          )}
        </ReactFlow>
      </div>

      {editingNode && (
        <NodeEditor
          node={editingNode}
          onUpdate={updateNodeData}
          onClose={() => setEditingNode(null)}
          onDelete={deleteNode}
        />
      )}
    </div>
  );
};

function toolbarBtnStyle(bg, color) {
  return {
    background: bg, color, border: "none", borderRadius: 8,
    padding: "8px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer",
    boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
  };
}

const Chatbotflowbuilder = () => (
  <ReactFlowProvider>
    <ChatbotflowbuilderInner />
  </ReactFlowProvider>
);

export default Chatbotflowbuilder;